import { AlertTriangle, PauseCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatMinutes } from '@/components/task-queue/BatchLaneTab';
import type { ParkedContentItem, ParkReason } from '@/contexts/TaskQueueContext';

// Ceilings mirror PARK_CEILINGS in shared/queue_states.py. Past its ceiling the
// park sweep releases a row whatever its batch artifact says.
const PARK_REASONS: Record<ParkReason, { label: string; ceilingMinutes: number | null }> = {
  transcript_batch: { label: 'Waiting for batch transcript', ceilingMinutes: 26 * 60 },
  call_batch: { label: 'Waiting for batch LLM call', ceilingMinutes: 26 * 60 },
  source_inactive: { label: 'Source paused', ceilingMinutes: null },
  probe: { label: 'Operator park probe', ceilingMinutes: 1 },
};

const NEAR_CEILING_FRACTION = 0.75;

function minutesSince(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, (Date.now() - then) / 60_000);
}

function AgeWarning({ ageMinutes, ceilingMinutes }: { ageMinutes: number | null; ceilingMinutes: number | null }) {
  if (ageMinutes === null || ceilingMinutes === null) return null;
  if (ageMinutes >= ceilingMinutes) {
    return (
      <span className="text-xs text-brick font-medium flex items-center gap-1" data-testid="park-age-warning">
        <AlertTriangle className="h-3 w-3" />
        Past {formatMinutes(ceilingMinutes)} ceiling. The park sweep should have released it.
      </span>
    );
  }
  if (ageMinutes >= ceilingMinutes * NEAR_CEILING_FRACTION) {
    return (
      <span className="text-xs text-amber font-medium flex items-center gap-1" data-testid="park-age-warning">
        <AlertTriangle className="h-3 w-3" />
        Nearing {formatMinutes(ceilingMinutes)} ceiling
      </span>
    );
  }
  return null;
}

interface ParkedTabProps {
  items: ParkedContentItem[];
  total: number;
}

/**
 * "Parked" tab of the Task Queue modal (CUR-1629): rows in `awaiting_batch`,
 * oldest first. They wait on a batch result without holding a worker slot,
 * which is why they appear in neither Active nor Queued.
 */
export function ParkedTab({ items, total }: ParkedTabProps) {
  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground space-y-1">
        <div className="flex items-center justify-center gap-2">
          <PauseCircle className="h-4 w-4" />
          No parked items
        </div>
        <p className="text-xs">
          A parked item waits on a batch result and holds no worker slot, so it never counts as
          active or queued.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {total > items.length && (
        <p className="text-xs text-muted-foreground">
          Showing the oldest {items.length} of {total} parked items.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground border-b">
              <th className="py-2 pr-4 font-medium">Content</th>
              <th className="py-2 pr-4 font-medium">Reason</th>
              <th className="py-2 font-medium">Parked</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const reason = PARK_REASONS[item.park_reason] ?? { label: item.park_reason, ceilingMinutes: null };
              const age = minutesSince(item.parked_at);
              return (
                <tr key={item.content_id} className="border-b last:border-0 align-top" data-testid="parked-row">
                  <td className="py-2 pr-4">
                    <div className="font-medium">{item.title}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {item.content_type}
                      </Badge>
                      <span className="text-xs text-muted-foreground font-mono">
                        {item.content_id.substring(0, 8)}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 pr-4">{reason.label}</td>
                  <td className="py-2 space-y-1">
                    <div className="text-xs text-muted-foreground">
                      {age === null ? 'unknown' : `${formatMinutes(age)} ago`}
                    </div>
                    <AgeWarning ageMinutes={age} ceilingMinutes={reason.ceilingMinutes} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
